/**
 * Node Health — single source of truth for address validity/reachability.
 *
 * Any component (NodeInspector, TriageRibbon, Omnibar, Workbench actions)
 * can call `getNodeHealth(nodeData)` to determine whether an address
 * is healthy, empty, or unresolvable — and gate UI accordingly.
 *
 * The derivation is purely from node data (no API calls), so it's safe
 * to call in render paths, useMemo, selectors, etc.
 */

import type { GraphNodeData } from '../types/graph';

// ── Types ────────────────────────────────────────────────────────────

export type NodeHealthStatus = 'healthy' | 'empty' | 'unresolvable';

export interface NodeHealth {
    /** Overall status */
    status: NodeHealthStatus;
    /** Human-readable explanation shown in banners */
    reason: string;
    /** Whether data-fetching actions (expand, triage, intelligence) should be blocked */
    blockFetches: boolean;
    /** Whether legal/export actions (notice, report) should be blocked */
    blockLegalActions: boolean;
    /** Whether investigation actions (label, flag, stash, watchlist) remain enabled */
    allowInvestigationActions: boolean;
}

// ── Derivation ───────────────────────────────────────────────────────

/**
 * Derive node health from GraphNodeData.
 *
 * Priority:
 *   1. `fetch_failed` tag + zero activity  → unresolvable
 *   2. Zero balance + zero tx_count + no expanded txs → empty
 *   3. Anything else → healthy
 *
 * Nodes freshly created from CSV import (no API call yet) have
 * `tx_count === undefined` — these are treated as "empty" (not unresolvable)
 * so the user can still attempt to expand them.
 */
export function getNodeHealth(node: GraphNodeData | null | undefined, storeTxCount?: number): NodeHealth {
    if (!node) {
        return {
            status: 'empty',
            reason: 'No node selected',
            blockFetches: false,
            blockLegalActions: true,
            allowInvestigationActions: false,
        };
    }

    const tags = node.entity_tags || (node as any).tags || [];
    const hasFetchFailed = tags.includes('fetch_failed');
    
    // Take the maximum of all available transaction count signals to be resilient
    const txCountSignals = [
        node.tx_count ?? 0,
        storeTxCount ?? 0,
        node.expanded_transactions?.length ?? 0
    ];
    const maxTxCount = Math.max(...txCountSignals);
    
    // Determine if we truly have zero activity (no txs AND no balance)
    const balance = node.balance ?? 0;
    const hasZeroActivity = maxTxCount === 0 && balance === 0;

    // Special case: if node.tx_count is undefined, it means we haven't even tried to load details yet
    // but if we have transactions in store or expanded, it's definitely not empty.
    const isPendingLoad = node.tx_count === undefined && maxTxCount === 0 && balance === 0;

    // Unresolvable: backend confirmed the address couldn't be resolved on-chain
    if (hasFetchFailed && hasZeroActivity) {
        return {
            status: 'unresolvable',
            reason: 'Address not found on-chain. Verify the address and chain are correct.',
            blockFetches: true,
            blockLegalActions: true,
            allowInvestigationActions: true, // Label/flag/stash still useful
        };
    }

    // Empty: valid format but no on-chain activity detected (yet)
    // This includes freshly-imported CSV addresses that haven't been expanded
    if ((hasZeroActivity || isPendingLoad) && !hasFetchFailed) {
        const emptyReason = (node as GraphNodeData).empty_reason;
        const chain = node.chain || 'this chain';
        const suggested = (node as GraphNodeData).suggested_chain;
        let reason = 'No on-chain activity detected. This address may be unused or newly created.';
        if (emptyReason === 'unsupported_protocol_stub') {
            reason = `No swaps on ${chain}: this bytecode is Uniswap UnsupportedProtocol (not the live router).`;
            if (suggested) {
                reason += ` Same address is active on ${suggested}.`;
            }
        }
        return {
            status: 'empty',
            reason,
            blockFetches: false,       // Let user attempt expand — address might have activity
            blockLegalActions: false,   // Allow — officer may still want a report
            allowInvestigationActions: true,
        };
    }

    // Healthy: has transactions or balance
    return {
        status: 'healthy',
        reason: '',
        blockFetches: false,
        blockLegalActions: false,
        allowInvestigationActions: true,
    };
}
